/**
 * Subdomain extraction and validation utilities
 */

// Cache for domain configuration
let cachedDomain: string | null = null;
let domainPromise: Promise<string> | null = null;

/**
 * Get the configured domain from platform settings
 * @returns The configured domain or default 'school-portal.com'
 */
async function getConfiguredDomain(): Promise<string> {
  // Return cached domain if available
  if (cachedDomain) {
    return cachedDomain;
  }

  // If a request is already in progress, return that promise
  if (domainPromise) {
    return domainPromise;
  }

  // Fetch domain from API
  domainPromise = (async () => {
    try {
      // Dynamically import to avoid circular dependencies
      const { platformSettingsApi } = await import('./api');
      const settings = await platformSettingsApi.getSettings();
      cachedDomain = settings.domain || 'school-portal.com';
      return cachedDomain;
    } catch (error) {
      console.warn('Failed to fetch domain from platform settings, using default:', error);
      cachedDomain = 'school-portal.com';
      return cachedDomain;
    } finally {
      domainPromise = null;
    }
  })();

  return domainPromise;
}

/**
 * Extract subdomain from hostname
 * @param hostname - The hostname (e.g., "school1.localhost:5175" or "school1.school-portal.com")
 * @returns The subdomain or null if no subdomain found
 */
export function extractSubdomain(hostname: string): string | null {
  if (!hostname) return null;

  // Remove port if present
  const hostnameWithoutPort = hostname.split(':')[0];

  // Split by dots
  const parts = hostnameWithoutPort.split('.');

  // For localhost development: subdomain.localhost
  if (parts.length >= 2 && parts[parts.length - 1] === 'localhost') {
    return parts[0];
  }

  // For production: subdomain.base-domain, where base-domain itself may have
  // multiple labels (e.g. "stg.e-bora.com"). Comparing just the last 2 labels
  // breaks for those — compare the full configured base domain as a suffix
  // instead, trying the longest/most specific candidates first.
  const knownDomains = [
    ...(cachedDomain ? [cachedDomain] : []),
    'stg.e-bora.com',
    'e-bora.com',
    'school-portal.com',
  ].sort((a, b) => b.length - a.length);

  for (const domain of knownDomains) {
    if (hostnameWithoutPort === domain) return null; // bare base domain, no subdomain
    if (hostnameWithoutPort.endsWith(`.${domain}`)) {
      return hostnameWithoutPort.slice(0, -(domain.length + 1));
    }
  }

  // No subdomain found (base domain)
  return null;
}

/**
 * Get current subdomain from window location, query params, or localStorage
 * @returns The current subdomain or null
 */
export function getCurrentSubdomain(): string | null {
  if (typeof window === 'undefined') return null;

  // First try to extract from hostname (production subdomain URLs)
  const hostnameSubdomain = extractSubdomain(window.location.hostname);
  if (hostnameSubdomain) {
    return hostnameSubdomain;
  }

  // For localhost development, check query parameter first (for password setup links)
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    const urlParams = new URLSearchParams(window.location.search);
    const querySubdomain = urlParams.get('subdomain');
    if (querySubdomain) {
      // Store in localStorage for subsequent navigation
      localStorage.setItem('currentSubdomain', querySubdomain);
      return querySubdomain;
    }

    // Fallback to localStorage
    const storedSubdomain = localStorage.getItem('currentSubdomain');
    if (storedSubdomain) {
      return storedSubdomain;
    }
  }

  return null;
}

/**
 * Validate subdomain format
 * @param subdomain - The subdomain to validate
 * @returns True if valid format, false otherwise
 */
export function isValidSubdomainFormat(subdomain: string): boolean {
  if (!subdomain) return false;

  // Subdomain rules:
  // - 3-63 characters
  // - Only lowercase letters, numbers, and hyphens
  // - Cannot start or end with hyphen
  // - Cannot contain consecutive hyphens
  const subdomainRegex = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

  return subdomainRegex.test(subdomain) && subdomain.length >= 3 && subdomain.length <= 63;
}

/**
 * Build subdomain URL
 * @param subdomain - The subdomain
 * @param path - The path (default: '/login')
 * @param isDevelopment - Whether in development mode
 * @returns The full URL with subdomain
 */
export async function buildSubdomainUrl(
  subdomain: string,
  path: string = '/login',
  isDevelopment?: boolean
): Promise<string> {
  // Auto-detect if not explicitly provided
  if (isDevelopment === undefined) {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    isDevelopment =
      hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('localhost:');
  }

  if (isDevelopment) {
    // Development: use localhost with subdomain as query parameter (no subdomain in hostname)
    // This avoids needing /etc/hosts entries for each tenant
    const port = typeof window !== 'undefined' ? window.location.port || '5173' : '5173';
    // Use query parameter approach for development
    const separator = path.includes('?') ? '&' : '?';
    return `http://localhost:${port}${path}${separator}subdomain=${subdomain}`;
  } else {
    // Production: subdomain.{configured-domain}
    // Example: olive.schoolapp.com/login
    const domain = await getConfiguredDomain();
    return `https://${subdomain}.${domain}${path}`;
  }
}

/**
 * Check if current URL has a subdomain or one is stored in localStorage
 * @returns True if subdomain is present, false otherwise
 */
export function hasSubdomain(): boolean {
  return getCurrentSubdomain() !== null;
}
